/**
 * FRONTROOMS First-Person Player Controller
 * Handles PointerLock mouse look, smooth WASD movement, AABB sliding collisions,
 * and realistic head bobbing/sway effects.
 */

export class PlayerController {
  constructor(camera, domElement, audioSynth) {
    this.camera = camera;
    this.domElement = domElement;
    this.audio = audioSynth;

    // Movement parameters
    this.walkSpeed = 2.4; // m/s
    this.friction = 12.0;  // Inertia damping
    this.acceleration = 120.0;
    this.height = 1.75;    // Human eye height in meters

    // Control states
    this.keys = { w: false, a: false, s: false, d: false };
    this.touchMoveVector = { x: 0, z: 0 };
    this.mouseSensitivity = 0.0022;
    this.yaw = 0;
    this.pitch = 0;

    // Physics vectors
    this.position = new camera.position.constructor(0, this.height, 0);
    this.velocity = new camera.position.constructor(0, 0, 0);

    // Set starting camera position immediately to render at eye height in start screen
    this.camera.position.set(this.position.x, this.height, this.position.z);

    // Collision data (List of physical AABBs: {minX, maxX, minZ, maxZ})
    this.collisionBoxes = [];
    this.playerRadius = 0.35; // Collision width

    // Head bobbing state
    this.bobTimer = 0;
    this.bobIntensity = 0.035; // Max displacement in meters
    this.bobFrequency = 9.0;    // Speed of bob cycle

    // Audio step tracking
    this.stepCycle = 0;
    this.stepInterval = 1.8; // Triggers sound every 1.8 units of movement

    // Setup listeners
    this.setupPointerLock();
    this.setupKeyboard();
    this.setupTouchControls();
  }

  /**
   * Links Pointer Lock API and mouse movement.
   */
  setupPointerLock() {
    this.domElement.addEventListener('click', () => {
      if (document.pointerLockElement !== this.domElement) {
        this.domElement.requestPointerLock();
      }
    });

    document.addEventListener('mousemove', (e) => {
      if (document.pointerLockElement !== this.domElement) return;

      this.yaw -= e.movementX * this.mouseSensitivity;
      this.pitch -= e.movementY * this.mouseSensitivity;

      // Clamp pitch to prevent turning upside down (-85 to +85 degrees)
      const limit = Math.PI / 2.1;
      this.pitch = Math.max(-limit, Math.min(limit, this.pitch));

      // Update camera rotation
      this.camera.rotation.set(0, 0, 0); // Reset base
      this.camera.quaternion.setFromEuler(new this.camera.rotation.constructor(this.pitch, this.yaw, 0, 'YXZ'));
    });
  }

  /**
   * Keyboard listeners.
   */
  setupKeyboard() {
    const handleKey = (e, isDown) => {
      switch (e.code) {
        case 'KeyW':
        case 'ArrowUp':
          this.keys.w = isDown;
          break;
        case 'KeyA':
        case 'ArrowLeft':
          this.keys.a = isDown;
          break;
        case 'KeyS':
        case 'ArrowDown':
          this.keys.s = isDown;
          break;
        case 'KeyD':
        case 'ArrowRight':
          this.keys.d = isDown;
          break;
      }
    };

    window.addEventListener('keydown', (e) => handleKey(e, true));
    window.addEventListener('keyup', (e) => handleKey(e, false));
  }

  /**
   * Sets up physical boxes to check collision against.
   * @param {Array} boxes - List of AABB boundaries
   */
  setCollisionBoxes(boxes) {
    this.collisionBoxes = boxes;
  }

  /**
   * Main player frame update. Handles inertia, collision testing, sliding, and camera bob.
   * @param {number} delta - Frame delta time in seconds
   */
  update(delta) {
    // 1. Calculate direction vector from keys
    const forward = new this.camera.position.constructor(0, 0, -1).applyQuaternion(this.camera.quaternion);
    forward.y = 0; // Walk horizontally only
    forward.normalize();

    const right = new this.camera.position.constructor(1, 0, 0).applyQuaternion(this.camera.quaternion);
    right.y = 0;
    right.normalize();

    const moveDirection = new this.camera.position.constructor(0, 0, 0);
    let touchSpeedRatio = 1.0;

    if (this.touchMoveVector && (this.touchMoveVector.x !== 0 || this.touchMoveVector.z !== 0)) {
      // Analog touch joystick movement: calculate and apply vector offsets
      moveDirection.addScaledVector(forward, -this.touchMoveVector.z);
      moveDirection.addScaledVector(right, this.touchMoveVector.x);

      const len = moveDirection.length();
      if (len > 0) {
        touchSpeedRatio = Math.min(1.0, len); // walking speed is scaled by drag length
        moveDirection.normalize();
      }
    } else {
      if (this.keys.w) moveDirection.add(forward);
      if (this.keys.s) moveDirection.sub(forward);
      if (this.keys.d) moveDirection.add(right);
      if (this.keys.a) moveDirection.sub(right);

      if (moveDirection.lengthSq() > 0) {
        moveDirection.normalize();
      }
    }

    // 2. Physics & Friction (Inertia)
    // Apply friction to slow down
    this.velocity.x -= this.velocity.x * this.friction * delta;
    this.velocity.z -= this.velocity.z * this.friction * delta;

    // Apply acceleration
    if (moveDirection.lengthSq() > 0) {
      this.velocity.x += moveDirection.x * this.acceleration * delta;
      this.velocity.z += moveDirection.z * this.acceleration * delta;
    }

    // Clamp speed to maximum walk speed (scaled dynamically by analog touch stick drift)
    const currentSpeed = Math.sqrt(this.velocity.x * this.velocity.x + this.velocity.z * this.velocity.z);
    const maxSpeed = this.walkSpeed * touchSpeedRatio;
    if (currentSpeed > maxSpeed) {
      this.velocity.x = (this.velocity.x / currentSpeed) * maxSpeed;
      this.velocity.z = (this.velocity.z / currentSpeed) * maxSpeed;
    }

    // 3. Collision Check & Axial Resolution (AABB Sliding)
    const nextX = this.position.x + this.velocity.x * delta;
    const nextZ = this.position.z + this.velocity.z * delta;

    // Check X Movement
    if (!this.checkCollision(nextX, this.position.z)) {
      this.position.x = nextX;
    } else {
      this.velocity.x = 0; // Halt X speed
    }

    // Check Z Movement
    if (!this.checkCollision(this.position.x, nextZ)) {
      this.position.z = nextZ;
    } else {
      this.velocity.z = 0; // Halt Z speed
    }

    // Sync physical position to Three.js camera position
    this.camera.position.x = this.position.x;
    this.camera.position.z = this.position.z;

    // 4. Footstep Sounds
    const actualHorizontalVelocity = Math.sqrt(this.velocity.x * this.velocity.x + this.velocity.z * this.velocity.z);
    if (actualHorizontalVelocity > 0.1) {
      // Step interval increment
      this.stepCycle += actualHorizontalVelocity * delta;
      if (this.stepCycle >= this.stepInterval) {
        this.stepCycle = 0;
        this.triggerFootstepAudio();
      }
    } else {
      // Fade step cycle down slowly if standing still
      this.stepCycle = Math.max(0, this.stepCycle - delta);
    }

    // 5. Highly Subtle Head Bobbing and Lateral Sway
    if (actualHorizontalVelocity > 0.1) {
      this.bobTimer += delta * this.bobFrequency;
      
      const bobY = Math.sin(this.bobTimer) * this.bobIntensity * (actualHorizontalVelocity / this.walkSpeed);
      const swayX = Math.cos(this.bobTimer * 0.5) * (this.bobIntensity * 0.4) * (actualHorizontalVelocity / this.walkSpeed);
      
      this.camera.position.y = this.height + bobY;
      this.camera.position.x += swayX * Math.cos(this.yaw);
      this.camera.position.z += swayX * Math.sin(this.yaw);
    } else {
      // Smoothly return eye height to 1.75m when stopping
      this.bobTimer = 0;
      this.camera.position.y = this.camera.position.y + (this.height - this.camera.position.y) * 8.0 * delta;
    }
  }

  /**
   * Collision check: Represent player as a circle of radius playerRadius.
   * Compares against all compiled AABBs.
   */
  checkCollision(px, pz) {
    const minX = px - this.playerRadius;
    const maxX = px + this.playerRadius;
    const minZ = pz - this.playerRadius;
    const maxZ = pz + this.playerRadius;

    for (let i = 0; i < this.collisionBoxes.length; i++) {
      const box = this.collisionBoxes[i];
      // AABB overlap check
      if (maxX > box.minX && minX < box.maxX &&
          maxZ > box.minZ && minZ < box.maxZ) {
        return true; // Overlap detected
      }
    }
    return false;
  }

  /**
   * Audits active zone floor material and triggers correct thud/click synthetic sound.
   */
  triggerFootstepAudio() {
    const xCoord = Math.floor((this.position.x + 10) / 20);
    const zCoord = Math.floor((this.position.z + 10) / 20);

    const val = Math.abs(this.hashCoord(xCoord, zCoord));
    const type = val % 10;

    let mat = 'carpet';
    if (type === 1 || type === 3 || type === 7 || type === 8 || type === 9) {
      mat = 'tile';
    } else if (type === 6) {
      mat = 'metal'; // Data center steel grid plates
    } else if (type === 2 && Math.random() > 0.8) {
      mat = 'metal'; // Steel floor plates occasionally
    }

    this.audio.playFootstep(mat);
  }

  /**
   * Initializes touchscreen listeners for mobile device overlays.
   * Maps D-pad touch buttons to movement keys and touch dragging to camera look.
   */
  setupTouchControls() {
    // 1. Movement Virtual Analog Touch Joystick (Left Side)
    const joystickContainer = document.getElementById('touch-joystick-container');
    const joystickHandle = document.getElementById('touch-joystick-handle');
    const handleImg = joystickHandle ? joystickHandle.querySelector('img') : null;

    if (joystickContainer && joystickHandle) {
      let touchId = null;
      let startX = 0;
      let startY = 0;
      const baseRadius = 50; // Max boundary drag radius in pixels

      const handleStart = (e) => {
        const rect = joystickContainer.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        for (let i = 0; i < e.changedTouches.length; i++) {
          const t = e.changedTouches[i];
          const dx = t.clientX - centerX;
          const dy = t.clientY - centerY;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < rect.width / 2 + 20) {
            touchId = t.identifier;
            startX = centerX;
            startY = centerY;
            joystickContainer.classList.add('active');
            joystickHandle.classList.add('active');
            if (handleImg) {
              handleImg.src = '/control icons/White/control_stick_blank_click.png';
            }
            updateJoystick(t.clientX, t.clientY);
            break;
          }
        }
      };

      const handleMove = (e) => {
        if (touchId === null) return;

        for (let i = 0; i < e.touches.length; i++) {
          const t = e.touches[i];
          if (t.identifier === touchId) {
            updateJoystick(t.clientX, t.clientY);
            break;
          }
        }
      };

      const handleEnd = (e) => {
        if (touchId === null) return;

        for (let i = 0; i < e.changedTouches.length; i++) {
          const t = e.changedTouches[i];
          if (t.identifier === touchId) {
            touchId = null;
            this.touchMoveVector = { x: 0, z: 0 };
            joystickContainer.classList.remove('active');
            joystickHandle.classList.remove('active');
            joystickHandle.style.transform = 'translate(-50%, -50%)'; // Snap back to center
            if (handleImg) {
              handleImg.src = '/control icons/White/control_stick_blank.png';
            }
            break;
          }
        }
      };

      const updateJoystick = (clientX, clientY) => {
        const dx = clientX - startX;
        const dy = clientY - startY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        let finalX = dx;
        let finalY = dy;

        if (dist > baseRadius) {
          finalX = (dx / dist) * baseRadius;
          finalY = (dy / dist) * baseRadius;
        }

        joystickHandle.style.transform = `translate(calc(-50% + ${finalX}px), calc(-50% + ${finalY}px))`;

        this.touchMoveVector = {
          x: finalX / baseRadius,
          z: finalY / baseRadius
        };
      };

      joystickContainer.addEventListener('touchstart', handleStart, { passive: false });
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd, { passive: false });
      window.addEventListener('touchcancel', handleEnd, { passive: false });
    }

    // 2. Action buttons touch controls (Right Half)
    const btnActionA = document.getElementById('touch-action-a');
    if (btnActionA) {
      btnActionA.addEventListener('touchstart', (e) => {
        e.preventDefault();
        // Dispatch custom KeyE down event to trigger interaction in World/Elevators
        const keyEventDown = new KeyboardEvent('keydown', { code: 'KeyE' });
        window.dispatchEvent(keyEventDown);
        
        // Quick visual press feedback
        btnActionA.classList.add('active');
      }, { passive: false });

      btnActionA.addEventListener('touchend', (e) => {
        e.preventDefault();
        const keyEventUp = new KeyboardEvent('keyup', { code: 'KeyE' });
        window.dispatchEvent(keyEventUp);
        btnActionA.classList.remove('active');
      }, { passive: false });
    }

    // Toggle menu/Pause panel using touch Menu button
    const btnMenu = document.getElementById('touch-menu');
    if (btnMenu) {
      btnMenu.addEventListener('touchstart', (e) => {
        e.preventDefault();
        // Dispatch ESC keydown to trigger Pause screen toggle in main.js
        const keyEvent = new KeyboardEvent('keydown', { code: 'Escape' });
        window.dispatchEvent(keyEvent);
      }, { passive: false });
    }

    // 3. Touch Drag Look trackpad (Right half of the screen)
    let lastTouchX = 0;
    let lastTouchY = 0;
    let isDragging = false;
    const touchSensitivity = 0.0035;

    const handleTouchStart = (e) => {
      // We only care about touch drags on the RIGHT half of the viewport
      const touch = e.changedTouches[0];
      if (touch.clientX >= window.innerWidth / 2) {
        lastTouchX = touch.clientX;
        lastTouchY = touch.clientY;
        isDragging = true;
      }
    };

    const handleTouchMove = (e) => {
      if (!isDragging) return;
      
      // Locate the active dragging touch on the right half
      let activeTouch = null;
      for (let i = 0; i < e.touches.length; i++) {
        if (e.touches[i].clientX >= window.innerWidth / 2) {
          activeTouch = e.touches[i];
          break;
        }
      }

      if (!activeTouch) return;

      const currentX = activeTouch.clientX;
      const currentY = activeTouch.clientY;

      const deltaX = currentX - lastTouchX;
      const deltaY = currentY - lastTouchY;

      // Update camera yaw & pitch
      this.yaw -= deltaX * touchSensitivity;
      this.pitch -= deltaY * touchSensitivity;

      // Clamp pitch to prevent turning upside down
      const limit = Math.PI / 2.1;
      this.pitch = Math.max(-limit, Math.min(limit, this.pitch));

      // Apply to camera
      this.camera.rotation.set(0, 0, 0);
      this.camera.quaternion.setFromEuler(new this.camera.rotation.constructor(this.pitch, this.yaw, 0, 'YXZ'));

      lastTouchX = currentX;
      lastTouchY = currentY;
    };

    const handleTouchEnd = () => {
      isDragging = false;
    };

    // Bind to window to allow dragging anywhere on the right half smoothly
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchEnd, { passive: true });
  }

  /**
   * Safe hashing utility to coordinate with generator mapping.
   */
  hashCoord(x, z) {
    let h1 = x * 374761393;
    let h2 = z * 668265263;
    let n = h1 + h2;
    n = (n ^ (n >> 13)) * 1274126177;
    return n ^ (n >> 16);
  }

  /**
   * Resets player back to coordinates (0, height, 0)
   */
  reset() {
    this.position.set(0, this.height, 0);
    this.velocity.set(0, 0, 0);
    this.yaw = 0;
    this.pitch = 0;
    this.camera.rotation.set(0, 0, 0);
    this.camera.position.copy(this.position);
  }
}
